import { useMutation } from "@apollo/client";
import Label from "@mui/icons-material/Label";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { EntityUpdate } from "components/ManageTags/types";
import WasteLineUnifiedTagPicker from "components/WasteLineUnifiedTagPicker";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useState } from "react";
import { useParams } from "react-router";

// GraphQL mutation for updating waste line tags in bulk
export const UPDATE_WASTE_LINE_TAGS_MUTATION = gql(`
  mutation UpdateWasteLineUnifiedTags($input: UpdateWasteLineUnifiedTagsInput!) {
    updateWasteLineUnifiedTags(input: $input)
  }
`);

const LOCAL_STORAGE_KEY = "waste-line-unified-recent-tags";

type WasteLineUnifiedRow = {
  id: string;
  tags?: string[];
};

type ManageWasteLineTagsProps = {
  selectedRows: WasteLineUnifiedRow[];
  dialogOpen: boolean;
  onClose: () => void;
};

// Menu item component
export function ManageWasteLineTagsMenuItem({
  selectedWasteLineIds,
  onClick,
}: {
  selectedWasteLineIds: string[];
  onClick: () => void;
}) {
  return (
    <MenuItem onClick={onClick} disabled={selectedWasteLineIds.length === 0}>
      <ListItemIcon>
        <Label />
      </ListItemIcon>
      <ListItemText>Manage Tags</ListItemText>
    </MenuItem>
  );
}

// Dialog component
export function ManageWasteLineTagsDialog({
  selectedRows,
  dialogOpen,
  onClose,
}: ManageWasteLineTagsProps) {
  const alerts = useAlerts();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const selectedWasteLineIds = selectedRows.map((row) => row.id);

  const [updateWasteLineTags, { loading }] = useMutation(
    UPDATE_WASTE_LINE_TAGS_MUTATION,
    {
      refetchQueries: ["WasteLinesUnified"],
    }
  );

  const getEntityId = useCallback((row: { id: string }) => row.id, []);

  const handleSubmit = useCallback(
    async (updates: EntityUpdate[]) => {
      if (!tenantId) {
        alerts.error("Tenant ID is required");
        return;
      }

      try {
        // Calculate tags to add and remove for each waste line
        const allTagsToAdd = new Set<string>();
        const allTagsToRemove = new Set<string>();

        updates.forEach((update) => {
          const currentRow = selectedRows.find((row) => row.id === update.id);
          const currentTags = new Set(currentRow?.tags || []);
          const newTags = new Set(update.tags);

          // Tags to add: in new but not in current
          newTags.forEach((tag) => {
            if (!currentTags.has(tag)) {
              allTagsToAdd.add(tag);
            }
          });

          // Tags to remove: in current but not in new
          currentTags.forEach((tag) => {
            if (!newTags.has(tag)) {
              allTagsToRemove.add(tag);
            }
          });
        });

        if (allTagsToAdd.size > 0 || allTagsToRemove.size > 0) {
          await updateWasteLineTags({
            variables: {
              input: {
                ids: selectedWasteLineIds,
                tenantId,
                tagsToAdd: Array.from(allTagsToAdd),
                tagsToRemove: Array.from(allTagsToRemove),
              },
            },
          });
        }

        const wasteLineUnifiedCount = selectedWasteLineIds.length;
        alerts.success(
          `Successfully updated tags for ${wasteLineUnifiedCount} ${pluralize(
            "manifest line item",
            wasteLineUnifiedCount
          )}`
        );

        onClose();
      } catch (err) {
        alerts.error(
          "An error occurred while updating the manifest line item tags",
          err
        );
      }
    },
    [
      updateWasteLineTags,
      alerts,
      onClose,
      tenantId,
      selectedWasteLineIds,
      selectedRows,
    ]
  );

  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    onClose();
  }, [onClose]);

  const confirm = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const onCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  if (!tenantId || !dialogOpen) {
    return null;
  }

  return (
    <ManageTagsFormDialog
      selectedRows={selectedRows}
      getEntityId={getEntityId}
      tenantId={tenantId}
      onClose={handleClose}
      onCloseConfirmation={onCloseConfirmation}
      confirm={confirm}
      open={dialogOpen}
      withConfirmation={true}
      showConfirmation={showConfirmation}
      onSubmit={handleSubmit}
      loading={loading}
      entityName="Manifest Line Item"
      localStorageKey={LOCAL_STORAGE_KEY}
      TagPickerComponent={WasteLineUnifiedTagPicker}
    />
  );
}
